import React, { useEffect, useState } from 'react';
import { formatDisplayDate, localISODate } from '../../utils/worklistDates';
import './SampleWorklist.css';

export const STATUS_LEGEND = [
  { key: 'pending', label: 'Pending' },
  { key: 'collected', label: 'Collected' },
  { key: 'received', label: 'Received' },
  { key: 'outsource', label: 'Outsourced' },
  { key: 'rejected', label: 'Rejected' },
];

/** From/To date inputs (values stay YYYY-MM-DD for the API). */
export const DateRangeFields = ({ idPrefix, fromDate, toDate, onFromChange, onToChange }) => (
  <>
    <div className="swl-filter-field">
      <label htmlFor={`${idPrefix}-from`}>From</label>
      <input
        id={`${idPrefix}-from`}
        type="date"
        value={fromDate}
        max={toDate || undefined}
        onChange={(e) => onFromChange(e.target.value)}
      />
    </div>
    <div className="swl-filter-field">
      <label htmlFor={`${idPrefix}-to`}>To</label>
      <input
        id={`${idPrefix}-to`}
        type="date"
        value={toDate}
        min={fromDate || undefined}
        onChange={(e) => onToChange(e.target.value)}
      />
    </div>
  </>
);

/** Readable date-range caption + compact status colour legend. */
export const WorklistContextBar = ({ fromDate, toDate, statusLabel }) => {
  const today = localISODate();
  let range;
  if (!fromDate && !toDate) range = 'All dates';
  else if (fromDate && fromDate === toDate) range = `${fromDate === today ? 'Today, ' : ''}${formatDisplayDate(fromDate)}`;
  else range = `${formatDisplayDate(fromDate) || 'Start'} → ${formatDisplayDate(toDate) || 'Today'}`;
  return (
    <div className="swl-context">
      <span className="swl-context-range" title="Counts and worklist are filtered by registration date">
        📅 Registered: <strong>{range}</strong>
        {statusLabel && (
          <>
            <span className="swl-context-sep" aria-hidden="true">·</span>
            Status: <strong>{statusLabel}</strong>
          </>
        )}
      </span>
      <ul className="swl-legend" aria-label="Status colour legend">
        {STATUS_LEGEND.map((s) => (
          <li key={s.key}>
            <span className={`swl-dot swl-dot-${s.key}`} aria-hidden="true" />
            {s.label}
          </li>
        ))}
      </ul>
    </div>
  );
};

/** Screen header: title, centre, user and a live clock (isolated so the page does not re-render every second). */
export const SampleWorklistHeader = ({ title, centreName, userName, children }) => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);
  return (
    <header className="swl-header">
      <div className="swl-header-title">
        <h1>{title}</h1>
        <div className="swl-header-meta">
          {centreName && <span>🏥 {centreName}</span>}
          {userName && <span>👤 {userName}</span>}
          <span>
            🕒{' '}
            {now.toLocaleString(undefined, {
              day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
            })}
          </span>
        </div>
      </div>
      <div className="swl-header-actions">{children}</div>
    </header>
  );
};

const KPI_CAPTIONS = {
  ALL: 'Tests registered',
  N: 'Awaiting collection',
  S: 'Awaiting receipt',
  Y: 'In laboratory',
  OUTSOURCE: 'Partner labs',
  R: 'Rejected specimens',
};
// Mutually exclusive workflow statuses used for the distribution bar (Outsourced is a flag, not a status)
const DISTRIBUTION_KEYS = ['N', 'S', 'Y', 'R'];

/**
 * Integrated KPI / status strip. Segments double as the status filter; all numbers are the
 * API totals for the current filters. `count === null` means that count failed to load.
 */
export const SampleSummaryCards = ({ cards, active, onSelect, loading, distributionKeys = DISTRIBUTION_KEYS }) => {
  const total = cards.find((c) => c.key === 'ALL')?.count;
  const share = (n) => (typeof n === 'number' && total > 0 ? Math.round((n / total) * 100) : null);
  const dist = cards.filter((c) => distributionKeys.includes(c.key) && c.count > 0);
  return (
    <section className="swl-kpi" aria-label="Status summary">
      <div className="swl-kpi-dist" aria-hidden="true">
        {total > 0 &&
          dist.map((c) => (
            <span key={c.key} className={`swl-kpi-dist-seg swl-tone-${c.tone}`} style={{ flexGrow: c.count }} />
          ))}
      </div>
      <nav className="swl-cards" aria-label="Status filters">
        {cards.map((c) => {
          const value = loading && c.count === undefined ? '…' : c.count === null || c.count === undefined ? '—' : c.count;
          const pct = c.key === 'ALL' ? null : share(c.count);
          return (
            <button
              key={c.key}
              type="button"
              className={`swl-card swl-tone-${c.tone} ${active === c.key ? 'active' : ''}`}
              onClick={() => onSelect(c.key)}
              aria-pressed={active === c.key}
              title={c.hint || c.label}
            >
              <span className="swl-card-label">{c.label}</span>
              <span className="swl-card-count">{value}</span>
              <span
                className="swl-card-rule"
                aria-hidden="true"
                style={{ '--swl-card-pct': pct !== null ? `${Math.min(Math.max(pct, 0), 100)}%` : '100%' }}
              />
              <span className="swl-card-caption">
                <span className="swl-card-dot" aria-hidden="true" />
                {c.caption || KPI_CAPTIONS[c.key] || ''}
                {pct !== null && <em>{pct}%</em>}
              </span>
            </button>
          );
        })}
      </nav>
    </section>
  );
};

/** Horizontal workflow stepper. Steps before `activeIndex` render as done. */
export const WorkflowStepper = ({ steps, activeIndex }) => (
  <ol className="swl-stepper" aria-label="Collection workflow">
    {steps.map((s, i) => {
      const state = i < activeIndex ? 'done' : i === activeIndex ? 'current' : 'todo';
      return (
        <li key={s.label} className={`swl-step swl-step-${state}`} aria-current={state === 'current' ? 'step' : undefined}>
          <span className="swl-step-num">{state === 'done' ? '✓' : i + 1}</span>
          <span className="swl-step-text">
            <strong>{s.label}</strong>
            {s.hint && <small>{s.hint}</small>}
          </span>
        </li>
      );
    })}
  </ol>
);

/** Empty / error panel for a worklist region. */
export const WorklistState = ({ type = 'empty', title, message, actionLabel, onAction }) => (
  <div className={`swl-state swl-state-${type}`} role={type === 'error' ? 'alert' : 'status'}>
    <div className="swl-state-icon" aria-hidden="true">{type === 'error' ? '⚠' : '🧪'}</div>
    <h3>{title}</h3>
    {message && <p>{message}</p>}
    {actionLabel && onAction && (
      <button type="button" className="btn btn-primary" onClick={onAction}>
        {actionLabel}
      </button>
    )}
  </div>
);
